import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import React from 'react';
import throttle from 'lodash/throttle';
import isEqual from 'deep-eql';
import { withMobileContext } from '@wix/da-react-context/pkg/MobileContext';
export const DEFAULT_SIZE = {
    desktop: {
        width: 1024,
        height: 768
    },
    mobile: {
        width: 375,
        height: 667
    }
};
const THROTTLE_TIMEOUT = 500;
const THROTTLE_OPTIONS = {
    trailing: true
};
export class Measured extends React.Component {
    getInitialStateContainer() {
        if (this.props.limitToParentWidth) {
            return this.getSSRContainer();
        }
        return this.isServerRendering() ? this.getSSRContainer() : undefined;
    }
    componentDidMount() {
        const { listenOnWindow , resizeHandler  } = this.props;
        this.mounted = true;
        if (listenOnWindow) {
            this.windowResizeListener = resizeHandler ? throttle(resizeHandler, THROTTLE_TIMEOUT, THROTTLE_OPTIONS) : this.updateContainerSize;
            window.addEventListener('resize', this.windowResizeListener);
        }
        this.updateContainerSize();
    }
    componentDidUpdate(prevProps, { container: prevContainer  }) {
        const { container  } = this.state;
        const { onUpdatedDims , cookieDim ='both'  } = this.props;
        if (!container || !onUpdatedDims) {
            return;
        }
        const widthChanged = [
            'width',
            'both'
        ].includes(cookieDim) && (!prevContainer || container.width !== prevContainer.width);
        const heightChanged = [
            'height',
            'both'
        ].includes(cookieDim) && (!prevContainer || container.height !== prevContainer.height);
        if (widthChanged || heightChanged) {
            onUpdatedDims(container);
        }
    }
    componentWillUnmount() {
        if (this.windowResizeListener) {
            window.removeEventListener('resize', this.windowResizeListener);
        }
        this.mounted = false;
    }
    isServerRendering() {
        return typeof document === 'undefined' || typeof window === 'undefined';
    }
    getDefaultSize() {
        const { defaultSize , isMobile  } = this.props;
        if (isMobile) {
            return (defaultSize || DEFAULT_SIZE).mobile;
        }
        return (defaultSize || DEFAULT_SIZE).desktop;
    }
    getSSRContainer() {
        const { prevLoadDims  } = this.props;
        if (prevLoadDims && (prevLoadDims.width > 0 || prevLoadDims.height > 0)) {
            return prevLoadDims;
        }
        return this.getDefaultSize();
    }
    getContainer() {
        // We are in browser from here
        if (this.props.measureWindow) {
            return {
                width: window.innerWidth,
                height: window.innerHeight
            };
        }
        // If the child component did not use our onRef, we use default size
        // This can be a slight optimisation in that we dont always measure a DOM el.
        if (!this.containerEl || !this.containerEl.getBoundingClientRect) {
            return this.getDefaultSize();
        }
        // We have specific element to measure
        const rect = this.containerEl.getBoundingClientRect();
        if (!rect.width && !rect.height) {
            return this.getDefaultSize();
        }
        return {
            width: rect.width,
            height: rect.height
        };
    }
    getSentinalWidth() {
        if (!this.sentinalRef.current) {
            return undefined;
        }
        return this.sentinalRef.current.getBoundingClientRect().width;
    }
    render() {
        const { container  } = this.state;
        if (this.props.limitToParentWidth) {
            return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
                ref: this.sentinalRef,
                style: {
                    maxWidth: '100%'
                }
            }), this.props.children({
                container,
                onRef: this.saveRefAndUpdateContainer
            }));
        }
        return this.props.children({
            container,
            onRef: this.saveRefAndUpdateContainer
        });
    }
    constructor(...args){
        super(...args);
        this.sentinalRef = /*#__PURE__*/ React.createRef();
        this.mounted = false;
        // For client-side we can skip first deviations rendering,
        // when we don`t know real dimensions to speed-up first rendering
        this.state = {
            container: this.getInitialStateContainer()
        };
        this.saveRefAndUpdateContainer = (node)=>{
            if (!node) {
                return;
            }
            this.containerEl = node;
            // Need to wait for mount to happen, so schedule for next frame
            // because this is called before mount because its a child ref
            // this fixes react warnings about setState on unMounted component.
            requestAnimationFrame(()=>this.updateContainerSize());
        };
        this.updateContainerSize = throttle(()=>{
            if (this.isServerRendering() || this.mounted === false) {
                return;
            }
            const { container  } = this.state;
            let newContainer = this.getContainer();
            const sentinalWidth = this.getSentinalWidth();
            // Make sure container is never bigger than parent
            // this allows us to shrink the container :)
            if (this.props.limitToParentWidth && sentinalWidth && sentinalWidth < newContainer.width) {
                newContainer = _object_spread_props(_object_spread({}, newContainer), {
                    width: sentinalWidth
                });
            }
            if (isEqual(container, newContainer)) {
                return;
            }
            this.setState({
                container: newContainer
            });
        }, THROTTLE_TIMEOUT, THROTTLE_OPTIONS);
    }
}
/** @deprecated */ export default withMobileContext(Measured);
